
/**
 * Per-workspace branding, applied as CSS custom properties on :root.
 *
 * Brand colours are also written onto the theme's own --accent / --accent-2 /
 * --bg tokens. Every component already paints from those, so branding reaches
 * the whole UI instead of only the few places that would think to read a
 * --brand-* variable — and nothing has to hardcode a colour to pick it up.
 */

const DEFAULT_NAME = "Qedara Chat";
const BRAND_VARS = ["--brand-primary", "--brand-accent", "--brand-font", "--brand-bg"];
const THEME_OVERRIDES = ["--accent", "--accent-2", "--bg"];

/** "#176CFF" | "#abc" -> "23 108 255", the triplet form the theme tokens use. */
function hexToTriplet(hex) {
  const m = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.exec(hex || "");
  if (!m) return null;
  let h = m[1];
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const n = parseInt(h, 16);
  return `${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255}`;
}

// Google Fonts are pulled once per family and left in place: a workspace switch
// back and forth should not re-request them.
function loadFont(family) {
  const id = `brand-font-${family}`;
  if (document.getElementById(id)) return;
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family)}:wght@400;500;600;700&display=swap`;
  document.head.appendChild(link);
}

export function brandName(ws) {
  return ws?.brand_name || DEFAULT_NAME;
}

export function applyBranding(ws) {
  const root = document.documentElement;
  // Clear first: switching to an unbranded workspace has to restore the theme.
  [...BRAND_VARS, ...THEME_OVERRIDES].forEach((v) => root.style.removeProperty(v));
  document.body.style.removeProperty("font-family");

  document.title = brandName(ws);
  if (!ws) return;

  if (ws.brand_primary_color) {
    root.style.setProperty("--brand-primary", ws.brand_primary_color);
    const t = hexToTriplet(ws.brand_primary_color);
    if (t) root.style.setProperty("--accent", t);
  }
  if (ws.brand_accent_color) {
    root.style.setProperty("--brand-accent", ws.brand_accent_color);
    const t = hexToTriplet(ws.brand_accent_color);
    if (t) root.style.setProperty("--accent-2", t);
  }
  if (ws.brand_background) {
    const t = hexToTriplet(ws.brand_background);
    if (t) {
      root.style.setProperty("--bg", t);          // a flat colour is just the canvas token
    } else {
      root.style.setProperty("--brand-bg", ws.brand_background);  // a gradient cannot be a triplet
    }
  }
  if (ws.brand_font) {
    if (ws.brand_font === "System") {
      root.style.setProperty("--brand-font", "system-ui, -apple-system, sans-serif");
    } else {
      loadFont(ws.brand_font);
      root.style.setProperty("--brand-font", `'${ws.brand_font}', system-ui, sans-serif`);
    }
    document.body.style.fontFamily = "var(--brand-font)";
  }
}
